import { createReducer, on } from '@ngrx/store';
import {
    deleteChannel,
    resetState,
    setActiveChannel,
    setChannelFilter,
    setTopicValidationKey,
    setUser,
    upsertActiveChannelAndMessage,
    upsertChannels,
    upsertMessages
} from "./messaging.actions";
import { Channel, ChannelFilter, Message } from "../../../core/models/messaging.models";
import moment from "moment/moment";
import { User } from "../../../core/user/user.types";

interface ChannelIdToChannelMap {
    [id: string]: Channel;
}

interface TopicValidationKeyMap {
    [topic: string]: number;
}

export interface MessagingState {
    user: User;
    activeChannelId: string;
    initialChannelMessageId: string;
    channelIds: string[];
    channelIdToChannelMap: ChannelIdToChannelMap;
    activeChannelMessages: Message[];
    channelFilter: ChannelFilter;
    topicValidationKeyMap: TopicValidationKeyMap;
    totalChannelLimit: number;
}

const initialState: MessagingState = {
    user: null,
    activeChannelId: null,
    initialChannelMessageId: null,
    channelIds: [],
    channelIdToChannelMap: {},
    activeChannelMessages: [],
    channelFilter: {},
    topicValidationKeyMap: {},
    totalChannelLimit: 30
};

export const messagingReducer = createReducer(
        initialState,
        on(resetState, (state) => ({
            ...initialState,
            user: state.user,
        })),
        on(setUser, (state, { user }) => {
            return {
                ...state,
                user: user
            }
        }),
        on(setActiveChannel, (state, { channelId, initialChannelMessageId }) => {
            console.log('reducer got setActiveChannel', channelId, initialChannelMessageId);
            return {
                ...state,
                activeChannelId: channelId,
                initialChannelMessageId: initialChannelMessageId,
                activeChannelMessages: [],
            };
        }),
        on(deleteChannel, (state, { channelId }) => {
            return {
                ...state,
                channelIds: state.channelIds.filter((id) => id !== channelId),
                channelIdToChannelMap: {
                    ...state.channelIdToChannelMap,
                    channelId: state.activeChannelId !== channelId ? undefined : state.channelIdToChannelMap[channelId]
                },
            }
            /* old implementation */
            // return {
            //     ...state,
            //     activeChannelId: state.activeChannelId === channelId ? null : state.activeChannelId,
            //     initialChannelMessageId: state.activeChannelId === channelId ? null : state.initialChannelMessageId,
            //     channelIds: state.channelIds.filter((id) => id !== channelId),
            //     channelIdToChannelMap: { ...state.channelIdToChannelMap, channelId: undefined },
            //     activeChannelMessages: state.activeChannelId === channelId ? [] : state.activeChannelMessages,
            // }
        }),
        on(upsertChannels, (state: MessagingState, { channels, totalChannelLimit }) => {
            console.log('upsertChannels', channels.map(c => `${c.name} ${c.tags?.gpt_suggestion_data?.reply}`).join(', '));
            let initialChannelLimit = state.totalChannelLimit;
            let stateChannelIds = state.channelIds.slice();
            let stateChannelIdToChannelMap = { ...state.channelIdToChannelMap };
            for (let channel of channels) {
                if (!stateChannelIds.includes(channel.id)) {
                    stateChannelIds.push(channel.id);
                }
                stateChannelIdToChannelMap[channel.id] = channel;
            }
            stateChannelIds.sort((a: string, b: string) => {
                return stateChannelIdToChannelMap[b].sort_index - stateChannelIdToChannelMap[a].sort_index;
            });
            stateChannelIds = stateChannelIds.slice(0, totalChannelLimit ?? initialChannelLimit);
            return {
                ...state,
                channelIds: stateChannelIds,
                channelIdToChannelMap: stateChannelIdToChannelMap,
                totalChannelLimit: totalChannelLimit ?? initialChannelLimit
            }
        }),
        on(upsertMessages, (state: MessagingState, { messages }) => {
            let stateMessages = state.activeChannelMessages.slice();
            messages.forEach((message) => {
                if (state.activeChannelId === message.channel_id) {
                    let messageIndex = stateMessages.findIndex((m) => m.id === message.id);
                    if (messageIndex === -1) {
                        stateMessages.push(message);
                    } else {
                        stateMessages[messageIndex] = message;
                    }
                }
            })
            stateMessages.sort((a: Message, b: Message) => {
                return moment(b.created).diff(a.created);
            })
            return {
                ...state,
                activeChannelMessages: stateMessages,
            }
        }),
        on(upsertActiveChannelAndMessage, (state: MessagingState, { message, channel }) => {
            if (state.activeChannelId === channel?.id) {
                /* TODO(handle case when message list is not at bottom) */
                /* TODO(upsert channel to channel ids list) */
                let stateMessages = state.activeChannelMessages.slice();
                let stateChannelIdToChannelMap = { ...state.channelIdToChannelMap };

                let messageIndex = stateMessages.findIndex((m) => m.id === message.id);
                if (messageIndex === -1) {
                    stateMessages.push(message);
                } else {
                    stateMessages[messageIndex] = message;
                }
                stateMessages.sort((a, b) => {
                    return moment(b.created).diff(a.created);
                })

                stateChannelIdToChannelMap[channel.id] = channel;
                return {
                    ...state,
                    channelIdToChannelMap: stateChannelIdToChannelMap,
                    activeChannelMessages: stateMessages,
                }
            } else {
                return state;
            }
        }),
        on(setChannelFilter, (state: MessagingState, { key, value }) => {
            let currentChannelFilter = { ...state.channelFilter };
            currentChannelFilter[key] = value;
            return {
                ...initialState,
                user: state.user,
                channelFilter: currentChannelFilter,
            }
        }),
        on(setTopicValidationKey, (state: MessagingState, { topic, key }) => {
            let currentTopicValidationKeyMap = { ...state.topicValidationKeyMap };
            currentTopicValidationKeyMap[topic] = key;
            return {
                ...state,
                topicValidationKeyMap: currentTopicValidationKeyMap
            }
        })
    )
;
